import { WidgetRuntimeConfigProvider } from "@/context/widget-runtime-config";
import { env } from "@/env";
import { useChatManager } from "@/hooks/use-chat-manager";
import { DISCLAIMER_TEXT } from "@/constants/disclaimer";
import { CHAT_PLACEHOLDER_TEXT } from "@/constants/search-constants";
import { resolvePlaceholder } from "@/utils/resolve-placeholder";
import { resolveSuggestedQuestions } from "@/utils/resolve-suggested-questions";
import {
  applyThemeVariables,
  applyThemeWithOverrides,
  buildAttributeOverrides,
  fetchWidgetTheme,
  injectBaseStyles,
  readWidgetAttributes,
} from "@/utils/widget-runtime";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import styled from "@emotion/styled";
import type { FC } from "react";
import { useRef, useState } from "react";
import ReactDOM from "react-dom/client";
import { Sentry } from "../../instrument";
import { ChatContent } from "../chat-components/chat-content";
import { SourcesModal } from "../chat-components/sources-modal";
import { colors, media, radius, spacing } from "../design-tokens";

const ChatContainer = styled.div`
  padding: ${spacing.xl}px;
  background-color: ${colors.surface};
  border-radius: ${radius.xl}px;

  ${media.sm} {
    padding: ${spacing["3xl"]}px;
  }
`;

const ChatComponent: FC = () => {
  const container = useRef<HTMLDivElement | null>(null);
  const chatManager = useChatManager();
  const [isSourcesOpen, setIsSourcesOpen] = useState(false);

  return (
    <>
      <ChatContainer ref={container}>
        <ChatContent {...chatManager} isSearchPage={false} onSourcesClick={() => setIsSourcesOpen(true)} />
      </ChatContainer>
      <SourcesModal
        isOpen={isSourcesOpen}
        onOpenChange={setIsSourcesOpen}
        sources={chatManager.response?.sources}
        container={container.current}
      />
    </>
  );
};

class ChatWebComponent extends HTMLElement {
  private root: ReactDOM.Root | null = null;
  private isMounted = false;
  private abortController: AbortController | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    this.isMounted = true;
    this.abortController?.abort();
    this.abortController = new AbortController();
    // Inject base styles including system fonts
    injectBaseStyles(this);

    const attributes = readWidgetAttributes(this);
    if (!attributes.apiKey) {
      console.error("Tenant UI Widget: api-key attribute is required.");
      return;
    }

    const apiUrl = attributes.apiUrl ?? env.API_URL;
    const runtimeConfig = {
      apiKey: attributes.apiKey,
      apiUrl,
      organizationSlug: undefined as string | undefined,
      widgetDisclaimerText: DISCLAIMER_TEXT,
      widgetSuggestedQuestions: resolveSuggestedQuestions(
        attributes.suggestedQuestions,
        undefined,
      ),
      showChatQuestions: true,
      widgetPlaceholderText: resolvePlaceholder(
        attributes.placeholderText,
        undefined,
        CHAT_PLACEHOLDER_TEXT,
      ),
      langfuse: null as { baseUrl: string; publicKey: string; environment?: string } | null,
    };

    const colorOverrides = buildAttributeOverrides(attributes);

    applyThemeVariables(this, colorOverrides, true);

    // Create a custom Emotion cache for this shadow root
    const cache = createCache({
      key: "shadow-emotion",
      container: this.shadowRoot as unknown as Element,
    });

    void fetchWidgetTheme(apiUrl, attributes.apiKey, this.abortController.signal).then(({ theme, disclaimerText, suggestedQuestions, showChatQuestions, placeholderText, organizationSlug, langfuse }) => {
      if (!this.isMounted) {
        return;
      }
      applyThemeWithOverrides(this, theme, colorOverrides);
      runtimeConfig.organizationSlug = organizationSlug ?? runtimeConfig.organizationSlug;
      runtimeConfig.widgetDisclaimerText = disclaimerText ?? runtimeConfig.widgetDisclaimerText;
      runtimeConfig.widgetSuggestedQuestions = resolveSuggestedQuestions(
        attributes.suggestedQuestions,
        suggestedQuestions,
      );
      runtimeConfig.showChatQuestions = showChatQuestions;
      runtimeConfig.widgetPlaceholderText = resolvePlaceholder(
        attributes.placeholderText,
        placeholderText,
        CHAT_PLACEHOLDER_TEXT,
      );
      runtimeConfig.langfuse = langfuse ?? null;
      if (this.root) {
        this.root.render(
          <CacheProvider value={cache}>
            <WidgetRuntimeConfigProvider value={runtimeConfig}>
              <Sentry.ErrorBoundary
                beforeCapture={(scope) => {
                  scope.setTag("component", "tenant-ai-widget-chat");
                  scope.setContext("widget", {
                    shadowDOM: true,
                    component: "ChatWebComponent",
                  });
                }}
              >
                <ChatComponent />
              </Sentry.ErrorBoundary>
            </WidgetRuntimeConfigProvider>
          </CacheProvider>,
        );
      }
    });

    this.root = ReactDOM.createRoot(this.shadowRoot as ShadowRoot);
    this.root.render(
      <CacheProvider value={cache}>
        <WidgetRuntimeConfigProvider value={runtimeConfig}>
          <Sentry.ErrorBoundary
            beforeCapture={(scope) => {
              scope.setTag("component", "tenant-ai-widget-chat");
              scope.setContext("widget", {
                shadowDOM: true,
                component: "ChatWebComponent",
              });
            }}
          >
            <ChatComponent />
          </Sentry.ErrorBoundary>
        </WidgetRuntimeConfigProvider>
      </CacheProvider>,
    );
  }

  disconnectedCallback() {
    this.isMounted = false;
    this.abortController?.abort();
    this.abortController = null;
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }
}

export default ChatWebComponent;
